/**
 * IconButton Component
 *
 * A button that contains only an icon, with proper accessibility.
 *
 * @example
 * ```tsx
 * <IconButton
 *   icon={<Plus />}
 *   aria-label="Add item"
 *   onClick={handleAdd}
 * />
 * ```
 */

import { Button, type ButtonProps } from '@heroui/react';
import { cn } from '@/lib/design-system';
import { forwardRef } from 'react';

interface IconButtonProps extends Omit<ButtonProps, 'children'> {
  /** The icon to display */
  icon: React.ReactNode;
  /** Accessible label (required for icon-only buttons) */
  'aria-label': string;
}

export const IconButton = forwardRef<HTMLButtonElement, IconButtonProps>(
  ({ icon, className, size = 'md', variant = 'light', ...props }, ref) => {
    const sizeClasses = {
      sm: 'min-w-8 w-8 h-8',
      md: 'min-w-10 w-10 h-10',
      lg: 'min-w-12 w-12 h-12',
    };

    return (
      <Button
        ref={ref}
        isIconOnly
        variant={variant}
        size={size}
        className={cn(sizeClasses[size as keyof typeof sizeClasses], className)}
        {...props}
      >
        {icon}
      </Button>
    );
  }
);

IconButton.displayName = 'IconButton';
